import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Category } from '../types';

// Query keys
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (storeId?: string) => [...categoryKeys.lists(), storeId] as const,
  tree: (storeId?: string) => [...categoryKeys.all, 'tree', storeId] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...categoryKeys.details(), id] as const,
};

// Fetch categories (flat list)
export function useCategories(storeId?: string, includeInactive = false) {
  const queryParams = new URLSearchParams();
  if (storeId) queryParams.set('storeId', storeId);
  if (includeInactive) queryParams.set('includeInactive', 'true');
  const queryString = queryParams.toString();

  return useQuery({
    queryKey: categoryKeys.list(storeId),
    queryFn: () => api.get<{ categories: Category[] }>(
      `/products/categories${queryString ? `?${queryString}` : ''}`
    ),
  });
}

// Fetch category tree (hierarchical)
export function useCategoryTree(storeId?: string) {
  const url = storeId 
    ? `/products/categories/tree?storeId=${storeId}`
    : '/products/categories/tree';

  return useQuery({
    queryKey: categoryKeys.tree(storeId),
    queryFn: () => api.get<{ categories: Category[] }>(url),
  });
}

// Fetch single category
export function useCategory(id: string) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => api.get<{ category: Category }>(`/products/categories/${id}`),
    enabled: !!id,
  });
}

// Create category
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Category>) =>
      api.post<{ category: Category }>('/products/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
  });
}

// Update category
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Category> }) =>
      api.put<{ category: Category }>(`/products/categories/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(variables.id) });
    },
  });
}

// Delete category
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: string; reassignTo?: string }) => {
      const url = reassignTo 
        ? `/products/categories/${id}?reassignTo=${reassignTo}`
        : `/products/categories/${id}`;
      return api.delete(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
  });
}

// Reorder categories
export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orders: { id: string; sortOrder: number }[]) =>
      api.post('/products/categories/reorder', { orders }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
  });
}
